import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/ToastContext";
import { DocumentLineItemWorkOrder } from "../types/Types";
import { parseDecimalInput, formatQtyForDisplay, formatMoney, formatUnitPrice } from "../utils/decimalInput";

function formatWorkOrderLine<T extends { purchasePrice?: unknown; valuationPrice?: unknown; quantity?: unknown; quantityOS?: unknown; quantityWorkOrder?: unknown; priceValue?: unknown }>(row: T): T {
  const editPrice = (value: unknown) => {
    const num = parseDecimalInput(value);
    if (num === null || num === 0) return "";
    return formatUnitPrice(num);
  };
  const showQty = (value: unknown) => formatQtyForDisplay(parseDecimalInput(value));
  const showMoney = (value: unknown) => {
    const num = parseDecimalInput(value);
    if (num === null) return "";
    return formatMoney(num);
  };
  const safeValuationPrice = row.valuationPrice !== null && row.valuationPrice !== undefined
    ? Math.max(0, parseDecimalInput(row.valuationPrice) ?? 0)
    : null;

  return {
    ...row,
    purchasePrice: editPrice(row.purchasePrice),
    valuationPrice: safeValuationPrice !== null ? editPrice(safeValuationPrice) : "",
    quantity: showQty(row.quantity),
    quantityOS: showQty(row.quantityOS),
    quantityWorkOrder: showQty(row.quantityWorkOrder),
    priceValue: showMoney(row.priceValue),
  } as T;
}

export const useDocumentLineItemWorkOrder = (documentId: string, archived?: boolean) => {
  const [data, setData] = useState<DocumentLineItemWorkOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL ?? "";
  const { showToast } = useToast();
  const { t } = useTranslation();
  // Confirm deletion: returns true if no nested rows, false otherwise
  const handleDeleteConfirm = useCallback(() => {
    if (data.length > 0) {
      // There are nested rows, so deletion should be blocked or confirmed
      return false;
    }
    return true;
  }, [data]);

  const fetchDocumentLineItemWorkOrder = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const params = new URLSearchParams();
      params.append("document", documentId);
      if (archived) params.append("archived", "true");

      const response = await axios.get(`${apiUrl}/api/document-line-item-work-order?${params.toString()}`, {
        headers: { "x-auth-token": token },
      });

      const formattedData = (response.data.data ?? []).map((row: DocumentLineItemWorkOrder) => formatWorkOrderLine(row));

      setData(formattedData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch document nested table");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, documentId, archived]);

  useEffect(() => {
    if (documentId) {
      fetchDocumentLineItemWorkOrder();
    } else {
      // Clear data when no document ID (new document)
      setData([]);
    }
  }, [fetchDocumentLineItemWorkOrder, documentId]);

  const createDocumentLineItemWorkOrder = async (payload: DocumentLineItemWorkOrder) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const res = await axios.post(`${apiUrl}/api/document-line-item-work-order`, payload, {
        headers: { "x-auth-token": token },
      });

      // Handle both single object and array responses (FEFO can create multiple rows)
      const newData = res.data.data;
      const formatted = Array.isArray(newData)
        ? newData.map((row: DocumentLineItemWorkOrder) => formatWorkOrderLine(row))
        : formatWorkOrderLine(newData);
      if (Array.isArray(formatted)) {
        setData((prev) => [...prev, ...formatted]);
      } else {
        setData((prev) => [...prev, formatted]);
      }

      showToast(t("toasts.itemCreated"), "success");
      return formatted;
    } catch (err: any) {
      showToast(err.response?.data?.message || t("toasts.genericError"), "error");
      setError(err.response?.data?.message || "Request failed");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editDocumentLineItemWorkOrder = async (id: string, payload: DocumentLineItemWorkOrder) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const res = await axios.put(`${apiUrl}/api/document-line-item-work-order/${id}`, payload, {
        headers: { "x-auth-token": token },
      });

      // update local data
      const formatted = formatWorkOrderLine(res.data.data);
      setData((prev) =>
        prev.map((row) => (row._id === id ? formatted : row))
      );

      showToast(t("toasts.itemUpdated"), "success");
      return formatted;
    } catch (err: any) {
      showToast(err.response?.data?.message || t("toasts.genericError"), "error");
      setError(err.response?.data?.message || "Failed to update");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteDocumentLineItemWorkOrder = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      await axios.delete(`${apiUrl}/api/document-line-item-work-order/${id}?actualDelete=true`, {
        headers: { "x-auth-token": token },
      });

      // Remove the item from local data
      setData(prev => prev.filter(row => row._id !== id));

      showToast(t("toasts.itemDeleted"), "success");
      // Refetch data to ensure consistency
      await fetchDocumentLineItemWorkOrder();

    } catch (err: any) {
      showToast(err.response?.data?.message || t("toasts.genericError"), "error");
      setError(err.response?.data?.message || "Failed to delete");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const archiveDocumentLineItemWorkOrder = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      await axios.delete(`${apiUrl}/api/document-line-item-work-order/${id}`, {
        headers: { "x-auth-token": token },
      });

      // If this is active data (not archived), remove the item from local data
      if (!archived) {
        setData(prev => prev.filter(row => row._id !== id));
      }
      
      // Refetch data to ensure consistency
      await fetchDocumentLineItemWorkOrder();

    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to archive");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { 
    data,
    loading,
    error,
    refetch: fetchDocumentLineItemWorkOrder,
    createDocumentLineItemWorkOrder,
    editDocumentLineItemWorkOrder,
    deleteDocumentLineItemWorkOrder,
    archiveDocumentLineItemWorkOrder,
    handleDeleteConfirm
  };
};

export default useDocumentLineItemWorkOrder;